'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import { isLighthouseClient } from '@/lib/isLighthouse';
import { observeForTaboolaLoad } from './taboolaActivation';

/**
 * Push a single command onto the Taboola queue. Safe to call before loader.js
 * has finished loading — `_taboola` is a plain array until the library replaces
 * it with its live API, and either form accepts `.push()`.
 */
export function pushTaboola(cmd: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  const w = window as unknown as {
    _taboola?: unknown[];
  };
  w._taboola = w._taboola || [];
  w._taboola.push(cmd);
}

// Taboola wants a single `{flush:true}` *after* all placements for a given
// "page" have been pushed. React mounts widgets in separate effects, so we
// debounce: every widget that mounts (re)arms this timer, and the flush fires
// once the burst settles. Infinite scroll re-arms it per scrolled-in article.
let flushTimer: ReturnType<typeof setTimeout> | null = null;
export function scheduleTaboolaFlush(): void {
  if (typeof window === 'undefined') return;
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => {
    flushTimer = null;
    pushTaboola({
      flush: true
    });
  }, 80);
}
interface TaboolaWidgetProps {
  /** Taboola display mode, e.g. `thumbnails-mid-3x2`, `alternating-thumbnails-a`. */
  mode: string;
  /** DOM id of the container div. Must be unique per page (Taboola requirement). */
  container: string;
  /** Backstage placement name, e.g. `Mid Homepage Thumbnails`. */
  placement: string;
  /** Taboola target type — `mix` for all onboarding placements. */
  targetType?: string;
  /**
   * Identifies which "page" this render belongs to (e.g. a section slug),
   * for placements whose `container`/`mode`/`placement` stay identical across
   * every page that uses them (Backstage's category/homepage placements are
   * configured against one fixed container id — unlike per-article
   * placements, it can't just be made unique per page). Without this, two
   * different pages using the same fixed props look identical to the effect's
   * dependency array, so if React reuses the component instance across a
   * client-side navigation (rather than unmounting it — not guaranteed even
   * with a changed `key`, depending on how the surrounding tree reconciles),
   * the push would never re-fire for the new page. Pass the section slug (or
   * any value that changes per page) so the "already pushed for THIS page"
   * check can tell the two apart even when every other prop matches.
   */
  pageKey?: string | number;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Renders one Taboola widget placement — the "body code" from the onboarding
 * snippet (`<div id=…>` + `_taboola.push({mode, container, placement, …})`),
 * ported to React. Self-skips the push on Lighthouse/PageSpeed (the container
 * div still renders, but stays empty, so layout is identical for synthetic runs).
 *
 * The container `<div>` always renders (SSR + client); only the queue push is
 * gated, and it re-fires whenever `pageKey` changes (not just once ever) —
 * see the pageKey doc comment above.
 */
export default function TaboolaWidget(t0) {
  const $ = _c(13);
  const {
    mode,
    container,
    placement,
    targetType: t1,
    pageKey,
    className,
    style
  } = t0;
  const targetType = t1 === undefined ? "mix" : t1;
  const pushedForKeyRef = useRef(undefined);
  const hasPushedRef = useRef(false);
  const containerRef = useRef(null);
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (isLighthouseClient()) {
        return;
      }
      return observeForTaboolaLoad(containerRef.current);
    };
    t3 = [];
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[2] !== container || $[3] !== mode || $[4] !== pageKey || $[5] !== placement || $[6] !== targetType) {
    t4 = () => {
      if (hasPushedRef.current && pushedForKeyRef.current === pageKey || isLighthouseClient()) {
        return;
      }
      hasPushedRef.current = true;
      pushedForKeyRef.current = pageKey;
      pushTaboola({
        mode,
        container,
        placement,
        target_type: targetType
      });
      scheduleTaboolaFlush();
    };
    t5 = [mode, container, placement, targetType, pageKey];
    $[2] = container;
    $[3] = mode;
    $[4] = pageKey;
    $[5] = placement;
    $[6] = targetType;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] !== className || $[10] !== container || $[11] !== style) {
    t6 = <div ref={containerRef} id={container} className={className} style={style} />;
    $[9] = className;
    $[10] = container;
    $[11] = style;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
}